import type { ReactNode } from 'react'

import type { FieldValues, Path } from 'react-hook-form'

import { type CommonFieldProps, type RadioOption, useRadio } from '@subaio/components/src/HookForm'

import { CardDescription } from './CardDescription'
import { CardDetails } from './CardDetails'
import { CardHeader } from './CardHeader'
import { CardIcon } from './CardIcon'
import { CardLabel } from './CardLabel'
import { CardRadio } from './CardRadio'
import { CardTitle } from './CardTitle'
import { Container } from './Container'
import { VisuallyHidden } from './VisuallyHidden'
import { Wrapper } from './Wrapper'

type Props<TPath extends string, TValue> = CommonFieldProps<TPath> & {
	options: Array<RadioOption<TValue> & { details?: ReactNode; icon: ReactNode }>
}

export const Application = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>, TValue>({
	name,
	label,
	hint,
	options: providedOptions,
	isRequired = false,
	isDisabled = false,
	isReadOnly = false,
	showOptionalHint: fieldShowOptionalHint,
	showChanged,
	validate = {},
}: Props<TPath, TValue>) => {
	const {
		hasError,
		error: fieldError,
		id,
		options,
	} = useRadio<TFieldValues, TPath, TValue>({
		name,
		options: providedOptions,
		isRequired,
		isDisabled,
		isReadOnly,
		showChanged,
		showOptionalHint: fieldShowOptionalHint,
		validate,
	})

	return (
		<Wrapper
			role='radiogroup'
			aria-describedby={fieldError?.message ? `${id}-error` : undefined}
			aria-invalid={hasError}
			aria-labelledby={`${id}-label`}
		>
			<VisuallyHidden id={`${id}-label`}>{label}</VisuallyHidden>
			{hint && <VisuallyHidden>{hint}</VisuallyHidden>}
			{options.map((option, index) => (
				<Container $isDisabled={Boolean(option.inputProps.disabled)} $isSelected={option.isChecked} key={option.id}>
					<CardLabel $isDisabled={Boolean(option.inputProps.disabled)} htmlFor={option.id}>
						<input type='radio' {...option.inputProps} name={name} value={String(option.value)} />
						<CardHeader>
							<CardIcon $isSelected={option.isChecked}>{providedOptions[index].icon}</CardIcon>
							<CardRadio $isSelected={option.isChecked} aria-hidden='true' />
						</CardHeader>
						<CardTitle>{option.label}</CardTitle>
						{option.hint && <CardDescription>{option.hint}</CardDescription>}
						{providedOptions[index].details && <CardDetails>{providedOptions[index].details}</CardDetails>}
					</CardLabel>
				</Container>
			))}
			{fieldError?.message && <VisuallyHidden id={`${id}-error`}>{fieldError.message}</VisuallyHidden>}
		</Wrapper>
	)
}
